import { cs } from '../../../lib/currency';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Wrench, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * iter334aj — Admin MI field-edit dialog.
 *
 * Uses `POST /api/admin/billing/mi-correct-amount` (existing endpoint,
 * extended to support `cc_surcharge` and PI-uniqueness gating). Lets
 * an admin repair MI-level data drift that the mainstream Edit Invoice
 * dialog cannot touch: stale Stripe PI, misapplied CC surcharge,
 * source-of-funds retagging.
 *
 * PI-uniqueness: if the new PI is already stamped on another live
 * invoice-shape row, the backend returns `pi_already_used` with the
 * conflict list. The dialog surfaces those rows and offers a "Stamp
 * anyway" force option.
 */
const PAYMENT_METHODS = [
  'stripe', 'card', 'credit', 'account_credit',
  'cash', 'cheque', 'check', 'zelle', 'venmo',
  'manual', 'offline', 'bank_transfer', 'wire', 'us_bank_account',
];

export default function AdminMiFieldEditDialog({ open, onOpenChange, invoice, onEdited }) {
  const miId = invoice?.monthly_invoice_id || invoice?.id;
  // iter334ak — Edit the MI's OWN PI, not the inherited-from-enrollment
  // one. Otherwise clearing the field succeeds silently because the
  // MI already has no PI of its own — the displayed PI lives on the
  // enrollment (line 734 of payments_invoice_list_helpers.py).
  const miOwnPi = invoice?.stripe_payment_intent_id_own || '';
  const enrPi = invoice?.stripe_payment_intent_id_enrollment || '';
  const piIsInherited = !!invoice?.stripe_payment_intent_id_inherited;
  const [form, setForm] = useState({
    stripe_payment_intent_id: '',
    cc_surcharge: '',
    payment_method: '',
    admin_notes: '',
    reason: '',
  });
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [forcePi, setForcePi] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setForm({
        stripe_payment_intent_id: miOwnPi,
        cc_surcharge: invoice.cc_surcharge != null ? String(invoice.cc_surcharge) : '',
        payment_method: invoice.payment_method || '',
        admin_notes: invoice.admin_notes || '',
        reason: '',
      });
      setConflicts([]);
      setForcePi(false);
    }
  }, [open, invoice, miOwnPi]);

  if (!invoice) return null;

  const buildCorrection = () => {
    const c = { mi_id: miId, reason: form.reason.trim() };
    if (form.stripe_payment_intent_id !== miOwnPi) {
      c.stripe_payment_intent_id = form.stripe_payment_intent_id.trim() || null;
      if (forcePi) c.force_pi = true;
    }
    if (form.cc_surcharge !== (invoice.cc_surcharge != null ? String(invoice.cc_surcharge) : '')) {
      const v = parseFloat(form.cc_surcharge);
      c.cc_surcharge = Number.isFinite(v) ? v : 0;
    }
    if (form.payment_method && form.payment_method !== (invoice.payment_method || '')) {
      c.payment_method = form.payment_method;
    }
    if (form.admin_notes !== (invoice.admin_notes || '')) {
      c.admin_notes = form.admin_notes;
    }
    return c;
  };

  const submit = async (dryRun) => {
    if (!form.reason.trim()) {
      toast.error('Please enter a reason (audit trail).');
      return;
    }
    const c = buildCorrection();
    const changed = Object.keys(c).filter((k) => !['mi_id', 'reason', 'force_pi'].includes(k));
    if (!changed.length) {
      toast.info('No changes to submit.');
      return;
    }
    setBusy(true);
    setConflicts([]);
    try {
      const res = await fetch('/api/admin/billing/mi-correct-amount', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ dry_run: dryRun, corrections: [c] }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body?.detail || 'Update failed');
        return;
      }
      const skipped = (body.skipped || [])[0];
      if (skipped?.reason === 'pi_already_used') {
        setConflicts(skipped.conflicts || []);
        toast.error('Stripe PI is already stamped on another invoice row.');
        return;
      }
      if (skipped) {
        toast.error(`Refused: ${skipped.reason}`);
        return;
      }
      if (dryRun) {
        toast.success('Preflight OK — click Apply to save.');
        return;
      }
      toast.success('MI updated.');
      onOpenChange(false);
      onEdited && onEdited();
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="admin-mi-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-blue-600" />
            Admin MI Field Edit
          </DialogTitle>
          <DialogDescription>
            Power-user edits on the underlying monthly_invoice: Stripe PI,
            CC surcharge, payment method, and admin notes. All changes
            are audit-logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            <span className="text-slate-500">MI ID:</span>{' '}
            <span className="font-mono">{miId}</span>
            {invoice.billing_period && (
              <> · <span className="text-slate-500">Month:</span> {invoice.billing_period}</>
            )}
          </div>

          <div>
            <Label htmlFor="pi">Stripe Payment Intent ID <span className="text-slate-400 font-normal">(MI-level)</span></Label>
            <Input
              id="pi"
              value={form.stripe_payment_intent_id}
              onChange={(e) => { setForm({ ...form, stripe_payment_intent_id: e.target.value }); setConflicts([]); setForcePi(false); }}
              placeholder={miOwnPi ? '' : '(MI has no PI of its own)'}
              data-testid="admin-mi-edit-pi"
              className="font-mono text-xs"
              disabled={busy}
            />
            <p className="text-[11px] text-slate-500 mt-0.5">
              Edits the MI&apos;s own PI. Empty string unlinks. Backend rejects duplicates across live invoices.
            </p>
            {piIsInherited && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1" data-testid="admin-mi-edit-inherited-warning">
                <div className="font-medium flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Displayed PI is inherited from the enrollment
                </div>
                <div className="font-mono text-[11px]">
                  Enrollment PI: {enrPi}
                </div>
                <div>
                  This MI has no PI of its own — the invoice list is showing the
                  enrollment&apos;s last-known Stripe charge as a fallback. The
                  enrollment PI is a legitimate historical record; leave it
                  alone. To make the display correct, set <strong>Payment
                  Method</strong> below to the actual method used (e.g.
                  <code className="mx-1">account_credit</code>) — the fallback
                  is auto-suppressed for non-card methods after this change.
                </div>
              </div>
            )}
          </div>

          {conflicts.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1" data-testid="admin-mi-edit-conflicts">
              <div className="font-medium flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                This PI is already stamped on {conflicts.length} live row{conflicts.length === 1 ? '' : 's'}:
              </div>
              <ul className="list-disc pl-4 space-y-0.5">
                {conflicts.map((c, i) => (
                  <li key={i} className="font-mono text-[11px]">
                    {c.collection} · id={String(c.id || '').slice(0, 8)} · status={c.status}
                    {c.billing_month ? ` · ${c.billing_month}` : ''}
                    {c.amount ? ` · ${cs()}${c.amount}` : ''}
                  </li>
                ))}
              </ul>
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forcePi}
                  onChange={(e) => setForcePi(e.target.checked)}
                  data-testid="admin-mi-edit-force-pi"
                />
                <span>
                  <strong>Stamp anyway</strong> — override the uniqueness guard. Only
                  do this after reconciling the conflicting rows above.
                </span>
              </label>
            </div>
          )}

          <div>
            <Label htmlFor="cc_surcharge">CC Surcharge ($)</Label>
            <Input
              id="cc_surcharge"
              type="number"
              step="0.01"
              value={form.cc_surcharge}
              onChange={(e) => setForm({ ...form, cc_surcharge: e.target.value })}
              data-testid="admin-mi-edit-cc-surcharge"
              disabled={busy}
            />
            <p className="text-[11px] text-slate-500 mt-0.5">
              Set to 0 when the MI was paid via non-card method (credit / check / cash).
            </p>
          </div>

          <div>
            <Label htmlFor="payment_method">Payment Method</Label>
            <Select
              value={form.payment_method || 'unchanged'}
              onValueChange={(v) => setForm({ ...form, payment_method: v === 'unchanged' ? '' : v })}
              disabled={busy}
            >
              <SelectTrigger data-testid="admin-mi-edit-payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unchanged">(leave unchanged)</SelectItem>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="admin_notes">Admin Notes</Label>
            <Textarea
              id="admin_notes"
              rows={2}
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              data-testid="admin-mi-edit-notes"
              disabled={busy}
            />
          </div>

          <div>
            <Label htmlFor="reason">Reason (required)</Label>
            <Textarea
              id="reason"
              rows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Aug PI mis-stamped on June credit-covered MI — unlinking + clearing surcharge"
              data-testid="admin-mi-edit-reason"
              disabled={busy}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="admin-mi-edit-cancel">
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => submit(true)} disabled={busy || !form.reason.trim()} data-testid="admin-mi-edit-preflight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Preflight
          </Button>
          <Button onClick={() => submit(false)} disabled={busy || !form.reason.trim()} data-testid="admin-mi-edit-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
